/**
 * Sentry Logger Service
 * Handles logging self-report events to Sentry for monitoring and analysis
 */

import * as Sentry from "@sentry/react";
import configuredStore from "store/store";
import { getSessionId, getTestTypeValue } from "store/slices/test";


interface SentryLogMessageOptions {
  message: string;
  level?: "debug" | "info" | "warning" | "error" | "fatal";
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

interface SentryLogExceptionOptions {
  error: Error | unknown;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

/**
 * Logs a message to Sentry
 * Creates a custom event that can be viewed in Sentry Issues
 * Automatically adds sessionId tag from Redux store
 * 
 * @param options - Message logging options containing message, level, tags, and extra data
 */
export const sentryLogMessage = (options: SentryLogMessageOptions): void => {
  try {
    const { message, level = "info", tags = {}, extra = {} } = options;

    // Get sessionId from Redux store
    const state = configuredStore.getState();
    const sessionId = getSessionId(state);
    const testType = getTestTypeValue(state);
    const appVersion = import.meta.env.VITE_VERSION;

    // Capture explicit timestamp for proper sorting in Sentry feed
    const timestamp = Date.now() / 1000; // Sentry expects Unix timestamp in seconds

    Sentry.withScope((scope) => {
      // Set explicit timestamp via event processor for proper chronological sorting
      scope.addEventProcessor((event) => {
        event.timestamp = timestamp;
        return event;
      });

      // Remove user context completely - set to null to ensure no user data
     const currentUser = scope.setUser(null);
      if (currentUser) {
        scope.setUser(null);
        scope.setContext("user", null);
        scope.setContext("geo", null);
        scope.setContext("ip", null);
        scope.setContext("userAgent", null);
      }

      const mergedTags = {
        ...(sessionId ? { sessionId: sessionId } : {}),
        ...(testType ? { testType: testType } : {}),
        ...(appVersion ? { app_version: appVersion } : {}),
        ...tags,
      };

      // Set tags for filtering
      if (Object.keys(mergedTags).length > 0) {
        scope.setTags(mergedTags);
      }

      // Set extra data for additional context
      if (Object.keys(extra).length > 0) {
        scope.setExtras(extra);
      }

      // Capture the message with the specified level
      // Timestamp is set via event processor above
      Sentry.captureMessage(message, level);
    });

    console.log(`✓ Sentry message logged: ${message}`);
  } catch (error) {
    // Silently fail - don't break the app if Sentry logging fails
    console.error("Failed to log message to Sentry:", error);
  }
};

/**
 * Extracts comprehensive error information for logging
 */
const extractErrorInfo = (error: unknown): {
  errorObject: Error;
  errorDetails: Record<string, unknown>;
} => {
  if (error instanceof Error) {
    // Preserve the original Error object with stack trace
    const errorDetails: Record<string, unknown> = {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };

    // Add cause if it exists (ES2022+ feature)
    if ('cause' in error && error.cause) {
      errorDetails.cause = error.cause;
    }

    return {
      errorObject: error,
      errorDetails,
    };
  }

  // Handle plain objects (e.g., API error responses)
  if (error && typeof error === "object") {
    const errorObj = error as Record<string, unknown>;
    
    // Safely extract message from nested properties
    const message = 
      (typeof errorObj.message === "string" ? errorObj.message : undefined) ||
      (errorObj.error && typeof errorObj.error === "object" && "message" in errorObj.error 
        ? String(errorObj.error.message) 
        : undefined) ||
      (errorObj.data && typeof errorObj.data === "object" && "message" in errorObj.data 
        ? String(errorObj.data.message) 
        : undefined) ||
      JSON.stringify(errorObj);
    
    const errorDetails: Record<string, unknown> = {
      originalError: errorObj,
      message: String(message),
    };

    // Preserve common error properties
    if (errorObj.statusCode) errorDetails.statusCode = errorObj.statusCode;
    if (errorObj.status) errorDetails.status = errorObj.status;
    if (errorObj.data) errorDetails.data = errorObj.data;
    if (errorObj.response) errorDetails.response = errorObj.response;
    if (errorObj.request) errorDetails.request = errorObj.request;

    return {
      errorObject: new Error(String(message)),
      errorDetails,
    };
  }

  // Handle strings or primitives
  const errorMessage = typeof error === "string" ? error : "Unknown error occurred";
  return {
    errorObject: new Error(errorMessage),
    errorDetails: {
      originalError: String(error),
      message: errorMessage,
    },
  };
};

/**
 * Logs an exception/error to Sentry
 * Creates an error event that can be viewed in Sentry Issues
 * Automatically adds sessionId tag from Redux store
 * Preserves full error trace including stack traces and error details
 * 
 * @param options - Exception logging options containing error, tags, and extra data
 */
export const sentryLogException = (options: SentryLogExceptionOptions): void => {
  try {
    const { error, tags = {}, extra = {} } = options;

    // Get sessionId from Redux store
    const state = configuredStore.getState();
    const sessionId = getSessionId(state);
    const testType = getTestTypeValue(state);
    const appVersion = import.meta.env.VITE_APP_VERSION;

    // Extract comprehensive error information
    const { errorObject, errorDetails } = extractErrorInfo(error);

    // Capture explicit timestamp for proper sorting in Sentry feed
    const timestamp = Date.now() / 1000; // Sentry expects Unix timestamp in seconds

    Sentry.withScope((scope) => {
      // Remove user context completely - set to null to ensure no user data
      scope.setUser(null);
      
      // Also clear any existing user context
      const currentUser = scope.getUser();
      if (currentUser) {
        scope.setUser(null);
        scope.setContext("user", null);
        scope.setContext("geo", null);
        scope.setContext("ip", null);
        scope.setContext("userAgent", null);
      }

      // Merge tags with sessionId (sessionId from options takes precedence if provided)
      const mergedTags = {
        ...(sessionId ? { sessionId: sessionId } : {}),
        ...(testType ? { testType: testType } : {}),
        ...(appVersion ? { app_version: appVersion } : {}),
        ...tags,
      };

      // Set tags for filtering
      if (Object.keys(mergedTags).length > 0) {
        scope.setTags(mergedTags);
      }

      // Merge error details with extra data
      const mergedExtra = {
        ...errorDetails,
        ...extra,
      };

      // Set extra data for additional context (includes error trace)
      if (Object.keys(mergedExtra).length > 0) {
        scope.setExtras(mergedExtra);
      }

      // Set error context for better trace visibility
      scope.setContext("error_details", {
        error_type: error instanceof Error ? error.constructor.name : typeof error,
        has_stack: error instanceof Error && !!error.stack,
        ...errorDetails,
      });

      // Use addEventProcessor to set timestamp before capturing
      scope.addEventProcessor((event) => {
        event.timestamp = timestamp;
        return event;
      });

      // Capture the exception - Sentry will parse stack trace automatically
      // The timestamp is set via the event processor above
      Sentry.captureException(errorObject);
    });

    console.log("✓ Sentry exception logged:", error);
  } catch (loggingError) {
    // Silently fail - don't break the app if Sentry logging fails
    console.error("Failed to log exception to Sentry:", loggingError);
  }
};